import { QuantitySelectorUpdateEvent } from '@theme/events';
import { StandardEvents } from '@shopify/events';

class OpticMergeVisionSet extends HTMLElement {
  async connectedCallback() {
    this.controller = new AbortController();
    this.productRoot = this.closest('.product-details');
    this.variants = JSON.parse(this.querySelector('[data-vs-variants]')?.textContent || '[]');
    this.unitPrice = Number(this.dataset.unitPrice);
    await customElements.whenDefined('quantity-selector-component');
    if (!this.isConnected) return;
    this.quantity = this.productRoot?.querySelector('product-form-component quantity-selector-component');
    if (typeof this.quantity?.setValue !== 'function') return;
    this.querySelector('fieldset').disabled = false;
    this.dataset.ready = 'true';
    const { signal } = this.controller;
    this.addEventListener('change', event => {
      if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
      const count = Number(event.target.value);
      this.quantity = this.productRoot?.querySelector('product-form-component quantity-selector-component');
      if (typeof this.quantity?.setValue !== 'function') return;
      this.quantity.setValue(String(count));
      this.quantity.updateButtonStates?.();
      this.quantity.dispatchEvent(new QuantitySelectorUpdateEvent(count));
      this.sync();
    }, { signal });
    this.productRoot.addEventListener('quantity-selector:update', () => this.sync(), { signal });
    document.addEventListener(StandardEvents.productSelect, event => {
      const variant = this.variants.find(v => String(v.id) === String(event.detail?.resource?.id));
      if (!variant) return;
      this.unitPrice = variant.price;
      this.updatePrices();
      requestAnimationFrame(() => this.sync());
    }, { signal });
    this.updatePrices();
    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.dataset.currency || 'USD', currencyDisplay: 'code'
    }).format(cents / 100);
  }

  updatePrices() {
    this.querySelectorAll('[data-vs-pack-total]').forEach(node => {
      node.textContent = this.money(this.unitPrice * Number(node.dataset.vsPackTotal));
    });
    this.querySelectorAll('[data-vs-unit]').forEach(node => { node.textContent = this.money(this.unitPrice); });
  }

  sync() {
    this.quantity = this.productRoot?.querySelector('product-form-component quantity-selector-component');
    if (!this.quantity) return;
    const values = this.quantity.getCurrentValues?.() || {min:1, step:1};
    const count = Number(this.quantity.getValue?.()) || 1;
    const maximum = this.quantity.getEffectiveMax?.() ?? values.max ?? Infinity;
    this.querySelectorAll('input[type="radio"]').forEach(input => {
      const value = Number(input.value);
      input.disabled = value < values.min || value > maximum || (value - values.min) % values.step !== 0;
      input.checked = value === count;
    });
    const label = count === 1 ? this.dataset.singleSummary : this.dataset.pluralSummary;
    const status = this.querySelector('[data-vs-set-summary]');
    if (status && label) status.textContent = label.replace('{quantity}', String(count)).replace('{total}', this.money(this.unitPrice * count));
  }
}

if (!customElements.get('om-vision-set')) customElements.define('om-vision-set', OpticMergeVisionSet);

class OpticMergeVisionColours extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.variants = JSON.parse(this.querySelector('[data-vs-colour-variants]')?.textContent || '[]');
    document.addEventListener(StandardEvents.productSelect, event => {
      const selected = this.variants.find(v => String(v.id) === String(event.detail?.resource?.id));
      if (!selected) return;
      this.querySelectorAll('[data-vs-colour]').forEach(link => {
        const colour = link.dataset.vsColour;
        const variant = this.variants.find(v => v.colour === colour && v.lens === selected.lens);
        if (variant) link.href = `${variant.url}#MainContent`;
        const current = colour === selected.colour;
        link.classList.toggle('is-selected', current);
        if (current) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }, { signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

if (!customElements.get('om-vision-colours')) customElements.define('om-vision-colours', OpticMergeVisionColours);
